<template>
  <!-- `persistent` : la modale ne se ferme que par ses boutons (ou Échap, qui vaut « Annuler »). -->
  <v-dialog :model-value="visible" :width="560" persistent scrollable @keydown.esc="annuler">
    <v-card class="pa-5">
      <div class="modele-titre pb-4">Choix du modèle de contrat</div>

      <div class="pb-2">
        Vous avez sélectionné <strong>{{ selectionnes }} contrat(s)</strong>.<br>
        Un modèle doit être choisi pour générer <strong>{{ aChoisir }} contrat(s)</strong> avant leur envoi en
        signature.
      </div>
      <div class="pb-3">Sélectionnez le modèle de contrat à utiliser.</div>

      <div class="modele-libelle pb-1">Modèle de contrat *</div>
      <v-card-text class="pa-0 modele-liste">
        <v-radio-group v-model="choix" :disabled="enCours" hide-details>
          <v-radio
v-for="modele in modeles"
                   :key="modele.id"
                   :value="modele.id"
                   color="secondary"
                   class="modele-option">
            <template #label>
              <div>
                <div class="modele-nom">{{ modele.name }}</div>
                <!-- Le modèle n'a pas de champ « description » : seul son titre de document peut en
                     tenir lieu, et seulement s'il apporte quelque chose de plus que le nom. -->
                <div v-if="descriptif(modele)" class="modele-descriptif">{{ descriptif(modele) }}</div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-card-text>

      <v-row class="ma-0 pt-5 d-flex justify-center" no-gutters>
        <div class="px-2">
          <loader-button
type="button"
                         label="Annuler"
                         :outlined="true"
                         :disabled="enCours"
                         @callback="annuler">
          </loader-button>
        </div>
        <div class="px-2">
          <loader-button
type="button"
                         :label="libelleValider"
                         :disabled="!valide"
                         :loading="enCours"
                         @callback="valider">
          </loader-button>
        </div>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from 'vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';

  /** Ce que la modale affiche d'un modèle de contrat (sous-ensemble de `ContratTemplate`). */
  export interface ModeleProposable {
    id: number;
    /** Libellé du modèle. */
    name: string;
    /** Titre du document généré, seul texte pouvant servir de descriptif. */
    titre?: string;
  }

  /**
   * Choix du modèle de contrat pour les contrats d'un envoi en signature en masse qui en demandent un
   * (OVA-4981).
   *
   * Purement présentationnel, comme SectionObjetCommunDialog : le parent fournit la liste des modèles
   * possibles — il ne les charge pas lui-même — et reçoit `valider(contratTemplateId)` ou `annuler`.
   * Rien n'est présélectionné : le bouton de validation reste inactif tant qu'aucun modèle n'est choisi
   * (règle de l'US).
   *
   * Le libellé du bouton (« Appliquer et continuer », arbitrage du 09/10/2026) et l'affichage du titre du
   * modèle comme descriptif (arbitrage en attente) restent paramétrables.
   */
  export default defineComponent({
    name: 'SectionModeleContratDialog',
    components: {
      LoaderButton,
    },
    props: {
      visible: {
        type: Boolean,
        required: false,
      },
      /** Nombre de contrats de la sélection (X de l'US). */
      selectionnes: {
        type: Number,
        required: true,
      },
      /** Nombre de contrats pour lesquels un modèle doit être choisi (Y de l'US). */
      aChoisir: {
        type: Number,
        required: true,
      },
      /** Modèles possibles, dans l'ordre où les proposer. */
      modeles: {
        type: Array as PropType<ModeleProposable[]>,
        required: true,
      },
      /** Afficher le titre du modèle sous son nom (arbitrage PO en attente). */
      afficherDescriptif: {
        type: Boolean,
        required: false,
        default: true,
      },
      /** « Appliquer et continuer » (arbitrage PO du 09/10/2026) : l'étape associe le modèle, la génération a lieu à l'envoi. */
      libelleValider: {
        type: String,
        required: false,
        default: 'Appliquer et continuer',
      },
      /** Enregistrement en cours : choix et « Annuler » figés, roue sur le bouton de validation. */
      enCours: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    emits: ['valider', 'annuler'],
    data () {
      return {
        choix: null as number | null,
      };
    },
    computed: {
      valide (): boolean {
        // Le modèle choisi doit encore faire partie de la liste : si le parent la met à jour (modèle
        // désactivé entre-temps), un choix devenu caduc ne doit pas pouvoir être validé.
        return this.choix != null && this.modeles.some((modele) => modele.id == this.choix) && !this.enCours;
      },
    },
    watch: {
      // Chaque ouverture repart sans sélection.
      visible (ouvert: boolean) {
        if (ouvert) {
          this.choix = null;
        }
      },
    },
    methods: {
      descriptif (modele: ModeleProposable): string {
        if (!this.afficherDescriptif) return '';
        const titre = (modele.titre ?? '').trim();
        return titre != '' && titre.toLowerCase() != modele.name.trim().toLowerCase() ? titre : '';
      },
      valider (): void {
        if (!this.valide) return;
        this.$emit('valider', this.choix);
      },
      annuler (): void {
        if (this.enCours) return;
        this.$emit('annuler');
      },
    },
  });
</script>

<style scoped>
  .modele-titre {
    font-size: 17px;
    font-weight: 600;
    color: #135253;
    text-align: center;
  }

  .modele-libelle {
    font-size: 14px;
    font-weight: 600;
    color: #135253;
  }

  .modele-liste {
    max-height: 320px;
    overflow-y: auto;
  }

  .modele-option {
    padding: 4px 0;
  }

  .modele-nom {
    font-weight: 600;
  }

  .modele-descriptif {
    font-size: 13px;
    color: #5B6A71;
  }
</style>
